import { useState } from "react";
import Enums from "../../utilities/enum";
import { DateRangePicker } from "../shared/dateRangePicker";
import { SearchSelect } from "../shared/searchSelect";
import stringHelpers from "../../utilities/stringHelpers";
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities";
import arrayUtilities from "../../utilities/arrayUtilities";
import { SearchFiltersModal } from "../shared/searchFiltersModal";
import { toDateString } from "../../utilities/dateUtilities";
import { MultiSearchSelect } from "../shared/searchSelectMulti";
import { TagSelector } from "../shared/tagSelector";
export const EnforcementDashboardFiltersModal = ({
	showModal,
	handleClose,
	handleConfirm,
	initialFilterStart,
	initialFilterEnd,
	initialType,
	initialStatuses,
	initialPreviousStatuses,
	initialContravention,
	initialIsOnHold,
	initialExcludeVoided,
	initialSelectedTags = [],
	initialAppliedFilters,
	contraventions,
	statusOptions,
	tags = []
}) => {
	const [filterStart, setFilterStart] = useState(initialFilterStart)
	const [filterEnd, setFilterEnd] = useState(initialFilterEnd)
	const [type, setType] = useState(initialType)
	const [selectedStatuses, setSelectedStatuses] = useState(initialStatuses)
	const [selectedPreviousStatuses, setSelectedPreviousStatuses] = useState(initialPreviousStatuses)
	const [contravention, setContravention] = useState(initialContravention)
	const [isOnHold, setIsOnHold] = useState(initialIsOnHold)
	const [excludeVoided, setExcludeVoided] = useState(initialExcludeVoided)
	const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
	const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

	const clearLocalFilters = () => {
		setFilterStart(null)
		setFilterEnd(null)
		setType(null)
		setSelectedStatuses([])
		setSelectedPreviousStatuses([])
		setContravention("")
		setIsOnHold(null)
		setExcludeVoided(true)
		setSelectedTags([])
		setAppliedFilters(null)
	}

	const statusItems = statusOptions
		.filter(item => !stringHelpers.isNullOrEmpty(item.value))
		.map(item => ({ id: item.value, name: item.label }))

	const handleTagsChange = tagSelections => {
		setSelectedTags(tagSelections)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
	}

	const handleStatusesChange = statuses => {
		setSelectedStatuses(statuses)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Status", statuses, statusItems))
	}

	const handlePreviousStatusesChange = statuses => {
		setSelectedPreviousStatuses(statuses)
		setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Previous Status", statuses, statusItems))
	}

	return (
		<SearchFiltersModal
			title="Filter Results"
			handleClose={ handleClose }
			handleConfirm={ () => handleConfirm({ filterStart, filterEnd, type, selectedStatuses, selectedPreviousStatuses, contravention, isOnHold, excludeVoided, selectedTags, appliedFilters }) }
			showModal={ showModal }
			key="filters-modal"
			handleReset={clearLocalFilters}
		>
			<div className="w-100">
				<div className="mb-2">
					<label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
					<DateRangePicker
						fromDate={ filterStart }
						toDate={ filterEnd }
						setDateRange={ (firstDate, lastDate, presetLabel) => {
							const isFirstDateEmpty = firstDate === null || firstDate === ""
							const isLastDateEmpty = lastDate === null || lastDate === ""
							setFilterStart(isFirstDateEmpty ? null : firstDate);
							setFilterEnd(isLastDateEmpty ? null : lastDate);
							if (!stringHelpers.isNullOrEmpty(presetLabel)) {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters.From
									delete updatedFilters.To
									updatedFilters["Date Range"] = presetLabel
									return updatedFilters
								})
							}
							else {
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!isFirstDateEmpty && !isLastDateEmpty) {
										delete updatedFilters.From
										delete updatedFilters.To
										updatedFilters["Date Range"] = `${toDateString(new Date(firstDate))} - ${toDateString(new Date(lastDate))}`
									} else {
										if (!isFirstDateEmpty) {
											updatedFilters.From = toDateString(new Date(firstDate))
										} else {
											delete updatedFilters.From
										}
										if (!isLastDateEmpty) {
											updatedFilters.To = toDateString(new Date(lastDate))
										} else {
											delete updatedFilters.To
										}
										delete updatedFilters["Date Range"]
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						}}
						showClearOption
						hideLabel
						fullWidth
						dropdownPositionRight
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					{/* Type */ }
					<label className="m-0 mr-2 mb-1 nowrap">Type</label>
					<select
						value={ stringHelpers.isNullOrEmpty(type) ? "" : type }
						onChange={ e => {
							setType(e.target.value)
							setAppliedFilters(prevState => {
								const updatedFilters = { ...prevState }
								if (stringHelpers.isNullOrEmpty(e.target.value)) {
									delete updatedFilters["Type"]
								} else {
									updatedFilters["Type"] = Enums.ContraventionTypes[e.target.value]
								}
								return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
							})
						} }
						className="border rounded px-2 py-1 w-100"
					>
						<option value={""}>All</option>
						{
							Object
							.entries(Enums.ContraventionTypes)
							.map(([key, value]) => <option value={key}>{value}</option>)
						}
					</select>
				</div>

				<div className="d-flex flex-column mb-2">
					{/* Status */ }
					<label className="m-0 mr-2 mb-1 nowrap">Status</label>
					<MultiSearchSelect
						width="100%"
						options={ statusOptions.filter(item => !stringHelpers.isNullOrEmpty(item.value)).map(item => ({ value: item.value, label: item.label })) }
						selectedValues={ selectedStatuses }
						defaultLabel="All"
						unit="statuses"
						onChange={ handleStatusesChange }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					{/* Previous Status */ }
					<label className="m-0 mr-2 mb-1 nowrap">Previous Status</label>
					<MultiSearchSelect
						width="100%"
						options={ statusOptions.filter(item => !stringHelpers.isNullOrEmpty(item.value)).map(item => ({ value: item.value, label: item.label })) }
						selectedValues={ selectedPreviousStatuses }
						defaultLabel="All"
						unit="statuses"
						onChange={ handlePreviousStatusesChange }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					{/* Contravention */ }
					<label className="m-0 mr-2 mb-1 nowrap">Contravention</label>
					<SearchSelect
						defaultLabel="All"
						width="100%"
						options={ [{ value: "", label: "All" }, ...contraventions.map(item => ({ value: item.id, label: item.name }))] }
						onSelect={ value => {
							if (!stringHelpers.isNullOrEmpty(value)) {
								setContravention(value)
								const selectedContravention = contraventions.find(item => item.id === value)
								setAppliedFilters(prevState => ({ ...prevState, "Contravention": selectedContravention.name }))
							} else {
								setContravention(null)
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									delete updatedFilters["Contravention"]
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							}
						} }
						selected={ !stringHelpers.isNullOrEmpty(contravention) ? contravention : null }
					/>
				</div>

				<div className="d-flex flex-column mb-2">
					{/* On Hold */ }
					<label className="m-0 mr-2 mb-1 nowrap">On Hold</label>
					<select
						onChange={ e => {
							if (e.target.value === "true") {
								setIsOnHold(true)
							} else if (e.target.value === "false") {
								setIsOnHold(false)
							} else {
								setIsOnHold(null)
							}
							setAppliedFilters(prevState => {
								const updatedFilters = { ...prevState }
								if (stringHelpers.isNullOrEmpty(e.target.value)) {
									delete updatedFilters["On Hold"]
								} else {
									updatedFilters["On Hold"] = e.target.value === "true" ? "Yes" : "No"
								}
								return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
							})
						} }
						value={ isOnHold === true ? "true" : isOnHold === false ? "false" : "" }
						className="border rounded px-2 py-1 w-100"
					>
						<option value="">All</option>
						<option value="true">On Hold</option>
						<option value="false">Not on hold</option>
					</select>
				</div>

				<div className="d-flex flex-column mb-2">

					<label className="m-0 mr-2 mb-1 nowrap">Exclude Voided</label>
					<div className="custom-control custom-switch">
						<input className="custom-control-input" type="checkbox" id="exclude-voided-toggle" checked={ excludeVoided }
							onChange={ e => {
								setExcludeVoided(e.target.checked)
								setAppliedFilters(prevState => {
									const updatedFilters = { ...prevState }
									if (!e.target.checked) {
										delete updatedFilters["Voids"]
									} else {
										updatedFilters["Voids"] = "Excluded"
									}
									return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
								})
							} }
						/>
						<label className="custom-control-label custom-toggle" htmlFor="exclude-voided-toggle"></label>
					</div>
				</div>

				{ !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
					<div className="d-flex align-items-center justify-content-between">
						<label className="m-0 mr-2 mb-1 nowrap">Tags</label>
						<button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
					</div>
					<TagSelector
						onChange={ handleTagsChange }
						selectedTags={ selectedTags }
						tags={ tags }
					/>
				</div> }
			</div>
		</SearchFiltersModal>
	)
}
